import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { homeFor } from "@/lib/i18n";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u.role), { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Connexion impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0A0D12] lg:block grain">
        <img src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Atelier" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-[#0A0D12]/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary shadow-[0_8px_24px_-8px_rgba(255,56,56,0.9)]"><Wrench className="h-5 w-5" /></span>
            <span className="font-heading text-xl font-extrabold">Concept By Yk</span>
          </div>
          <div className="max-w-lg rise">
            <p className="eyebrow !text-primary">CRM intelligent · Atelier automobile</p>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight sm:text-5xl">Toute l'activité de l'atelier, un seul outil.</h1>
            <p className="mt-4 text-base text-slate-300">Planning, ordres de réparation, devis, pièces, facturation Billit et assistant IA fondé sur vos données réelles.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 rise" data-testid="login-form">
          <div>
            <h2 className="font-heading text-2xl font-bold">Connexion</h2>
            <p className="mt-1 text-sm text-muted-foreground">Accédez à votre espace selon votre rôle.</p>
          </div>
          <div className="space-y-3">
            <Input type="email" placeholder="Adresse email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 rounded-xl" data-testid="login-email-input" autoComplete="username" />
            <Input id="login-password" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 rounded-xl" data-testid="login-password-input" autoComplete="current-password" />
          </div>
          {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-500" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="h-12 w-full rounded-xl text-base font-bold" data-testid="login-submit-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
